import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import PropTypes from 'prop-types';

import LoadingScreen from '../../../../collums-components/components/common/loadingScreen';
import { toLocaleString } from '../../../../collums-components/helpers';
import ChangeDirectDebitStatus from './ChangeDirectDebitStatus';
import { buttonsStyles } from '../../../../collums-constants/styles/stylesheets/buttonsStyles';

import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    withStyles,
    TableSortLabel,
    TablePagination,
    Paper,
    TableContainer,
    Button,
    makeStyles,
    TextField
} from '@material-ui/core';
import { accountViewStyles as styles } from './styles';
import ReattemptPaymentModal from './ReattemptPaymentModal';

import { StyledTableRow } from '../StyledTableRow';
import tableHeaders from './tableHeaders';
import {
    listCustomerMemberships,
    simulateAuthorize,
    simulatePaymentReceived,
    simulateInsufficientFunds,
    reattemptDirectDebitPayment
} from '../../../../api/membershipApi';
import { loadInvoice, setListShouldUpdate } from '../../../../actions/invoiceActions';
import Scrollbar from 'react-scrollbars-custom';

const isDev = ['dev', 'development', 'staging'].includes(
    process.env.REACT_APP_ENVIRONMENT_NAME || process.env.NODE_ENV
);

const MembershipView = ({ classes }) => {
    const [memberships, setMemberships] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const customer = useSelector(state => state.customer.customer);
    const dispatch = useDispatch();
    const [showChangeDirectDebitStatusModal, setShowChangeDirectDebitStatusModal] = useState(null);
    const [showReattemptModal, setShowReattemptModal] = useState(null);
    const [reattemptLoading, setReattemptLoading] = useState(false);
    const [simulateDates, setSimulateDates] = useState({});

    const shouldListUpdate = useSelector(state => state.invoice.shouldListUpdate);
    const globalStyles = makeStyles(buttonsStyles)();

    const [order, setOrder] = useState({
        order: 'desc',
        orderBy: 'invoice.createdAt'
    });

    const [isLoading, setIsLoading] = useState(false);

    const tableRef = useRef(null);
    const [tableHeight, setTableHeight] = useState(0);
    useEffect(() => {
        setTimeout(() => {
            if (tableRef.current) {
                setTableHeight(tableRef.current.offsetHeight);
            }
        }, 1000);
        // eslint-disable-next-line
    }, [tableRef.current]);

    const handleReorder = headCellId => {
        const data = {
            order: order?.order === 'asc' ? 'desc' : 'asc',
            orderBy: headCellId
        };

        fetch(data);
        setOrder(data);
    };

    useEffect(() => {
        if (!customer) return;
        fetch();
        /* eslint-disable-next-line */
    }, [customer, page, rowsPerPage]);

    useEffect(() => {
        if (shouldListUpdate) {
            fetch();
            dispatch(setListShouldUpdate(false));
        }
        /*eslint-disable-next-line */
    }, [shouldListUpdate]);

    const openInvoice = invoice => {
        dispatch(loadInvoice(invoice.id));
    };

    const fetch = async orderParam => {
        try {
            setIsLoading(true);
            const state = orderParam || order;
            const queryOrder = `${state.order === 'asc' ? '' : '-'}${state.orderBy}`;
            const response = await listCustomerMemberships({
                customer: customer.id,
                order: queryOrder,
                page,
                rowsPerPage
            });
            setMemberships(response.data);
            setCount(response.count);
            setIsLoading(false);
        } catch (e) {
            toastr.error('Something went wrong (code: c0033b)');
        }
    };

    return (
        <>
            {isLoading && <LoadingScreen />}
            <Box m={4}>
                <Box>
                    <TableContainer component={Paper}>
                        <Scrollbar style={{ width: '100%', height: tableHeight + 60 }} noScrollY={true}>
                            <Table className={classes.table} size="medium" stickyHeader ref={tableRef}>
                                <TableHead>
                                    <TableRow>
                                        {tableHeaders.map(headCell => (
                                            <TableCell
                                                className={classes.tableHeader}
                                                key={headCell.id}
                                                align={headCell.numeric ? 'right' : 'left'}
                                                padding={headCell.disablePadding ? 'All' : 'default'}
                                                sortDirection={order.orderBy === headCell.id ? order.order : false}
                                            >
                                                {headCell.reordable ? (
                                                    <TableSortLabel
                                                        active={order.orderBy === headCell.id}
                                                        direction={order.orderBy === headCell.id ? order.order : 'asc'}
                                                        onClick={() => handleReorder(headCell.id)}
                                                    >
                                                        {headCell.label}
                                                        {order.orderBy === headCell.id ? (
                                                            <span hidden>
                                                                {order.order === 'desc'
                                                                    ? 'sorted descending'
                                                                    : 'sorted ascending'}
                                                            </span>
                                                        ) : null}
                                                    </TableSortLabel>
                                                ) : (
                                                    headCell.label
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {memberships.map((membership, index) => {
                                        return (
                                            <StyledTableRow key={`${index}-body-table-row`}>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.capitalize} ${
                                                        ['overdue'].includes(membership.status) ||
                                                        ['insufficient_funds', 'payment_reattempted'].includes(
                                                            membership.coherentStatus
                                                        )
                                                            ? classes.fontColorRed
                                                            : ''
                                                    }`}
                                                >
                                                    {membership.status}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership?.createdAt
                                                        ? membership.createdAt.format('DD/MM/YYYY HH:mm')
                                                        : ''}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership?.formCompletedAt
                                                        ? membership.formCompletedAt.format('DD/MM/YYYY HH:mm')
                                                        : ''}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership.name}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership?.invoice?.code && (
                                                        <Button
                                                            color="primary"
                                                            className={classes.invoiceButton}
                                                            onClick={() => openInvoice(membership.invoice)}
                                                        >
                                                            {membership.invoice.code}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned}`}
                                                >
                                                    {toLocaleString(membership.amount)}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned}`}
                                                >
                                                    {membership.numberOfPayments
                                                        ? membership.numberOfPayments
                                                        : 'Until cancellation'}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership?.firstPayment
                                                        ? membership.firstPayment.format('DD/MM/YYYY')
                                                        : ''}
                                                </TableCell>
                                                <TableCell className={classes.tableBodyCell}>
                                                    {membership?.latestPayedAt
                                                        ? membership.latestPayedAt.format('DD/MM/YYYY')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned}`}
                                                >
                                                    {membership.paymentsTaken}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned}`}
                                                >
                                                    {membership.numberOfPayments ? membership.paymentsLeft : ''}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned}`}
                                                >
                                                    {toLocaleString(membership.amountPaid)}
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableBodyCell} ${classes.rightAligned} ${
                                                        membership.amountOutstanding ? classes.fontColorRed : ''
                                                    }`}
                                                >
                                                    {membership.amountOutstanding
                                                        ? toLocaleString(membership.amountOutstanding)
                                                        : ''}
                                                </TableCell>

                                                {/*<TableCell className={classes.tableBodyCell}>*/}
                                                {/*    {membership.soldBy?.displayName}*/}
                                                {/*</TableCell>*/}
                                                <TableCell className={classes.tableBodyCell}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            gap: 8
                                                        }}
                                                    >
                                                        {membership.coherentStatus === 'insufficient_funds' && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setShowReattemptModal({
                                                                        itemId: membership.itemId,
                                                                        type: membership.type
                                                                    });
                                                                }}
                                                            >
                                                                Reattempt payment
                                                            </Button>
                                                        )}
                                                        {!['void'].includes(membership.status) && (
                                                            <Button
                                                                className={globalStyles.redButton}
                                                                onClick={() => {
                                                                    setShowChangeDirectDebitStatusModal({
                                                                        itemId: membership.itemId,
                                                                        type: membership.type
                                                                    });
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        )}
                                                        {isDev &&
                                                            membership.type === 'membership' &&
                                                            !['void'].includes(membership.status) &&
                                                            membership.coherentStatus !== 'paid' && (
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label="Sim date"
                                                                    value={simulateDates[membership.itemId] || ''}
                                                                    onChange={e => {
                                                                        const val = e.target.value;
                                                                        setSimulateDates(prev => ({
                                                                            ...prev,
                                                                            [membership.itemId]: val
                                                                        }));
                                                                    }}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    style={{ width: 160 }}
                                                                    inputProps={{
                                                                        style: { padding: '6px 8px', fontSize: 12 }
                                                                    }}
                                                                />
                                                            )}
                                                        {isDev &&
                                                            membership.type === 'membership' &&
                                                            membership.coherentStatus !== 'authorised' &&
                                                            membership.coherentStatus !== 'paid' &&
                                                            !['void'].includes(membership.status) && (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        try {
                                                                            const simDate =
                                                                                simulateDates[membership.itemId] ||
                                                                                undefined;
                                                                            await simulateAuthorize(
                                                                                membership.itemId,
                                                                                simDate
                                                                            );
                                                                            toastr.success(
                                                                                'Membership authorized (simulated)'
                                                                            );
                                                                            fetch();
                                                                        } catch (e) {
                                                                            toastr.error(
                                                                                e?.response?.data?.message ||
                                                                                    'Failed to simulate authorization'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Authorize
                                                                </Button>
                                                            )}
                                                        {isDev &&
                                                            membership.type === 'membership' &&
                                                            membership.coherentStatus === 'authorised' && (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        try {
                                                                            const simDate =
                                                                                simulateDates[membership.itemId] ||
                                                                                undefined;
                                                                            await simulatePaymentReceived(
                                                                                membership.itemId,
                                                                                simDate
                                                                            );
                                                                            toastr.success(
                                                                                'Payment received (simulated)'
                                                                            );
                                                                            fetch();
                                                                        } catch (e) {
                                                                            toastr.error(
                                                                                e?.response?.data?.message ||
                                                                                    'Failed to simulate payment'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Simulate payment
                                                                </Button>
                                                            )}
                                                        {isDev &&
                                                            membership.type === 'membership' &&
                                                            membership.coherentStatus === 'authorised' && (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    style={{ color: 'red', borderColor: 'red' }}
                                                                    onClick={async () => {
                                                                        try {
                                                                            await simulateInsufficientFunds(
                                                                                membership.itemId
                                                                            );
                                                                            toastr.success(
                                                                                'Insufficient funds (simulated)'
                                                                            );
                                                                            fetch();
                                                                        } catch (e) {
                                                                            toastr.error(
                                                                                e?.response?.data?.message ||
                                                                                    'Failed to simulate insufficient funds'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Sim insufficient funds
                                                                </Button>
                                                            )}
                                                    </div>
                                                </TableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Scrollbar>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={(e, page) => {
                            setPage(page);
                        }}
                        onChangeRowsPerPage={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        classes={{ root: classes.tablePagination }}
                    />
                </Box>
            </Box>
            <ChangeDirectDebitStatus
                showChangeDirectDebitStatusModal={showChangeDirectDebitStatusModal}
                setShowChangeDirectDebitStatusModal={setShowChangeDirectDebitStatusModal}
            />
            <ReattemptPaymentModal
                data={showReattemptModal}
                onClose={() => setShowReattemptModal(null)}
                loading={reattemptLoading}
                onConfirm={async () => {
                    setReattemptLoading(true);
                    try {
                        await reattemptDirectDebitPayment(showReattemptModal.itemId, showReattemptModal.type);
                        toastr.success('Direct Debit payment will be reattempted');
                        setShowReattemptModal(null);
                        fetch();
                    } catch (e) {
                        toastr.error(e?.response?.data?.message || 'Failed to reattempt payment');
                    } finally {
                        setReattemptLoading(false);
                    }
                }}
            />
        </>
    );
};

MembershipView.propTypes = {
    classes: PropTypes.object.isRequired
};

export default withStyles(styles)(MembershipView);
